import React from 'react';

import {ForecastTime} from 'src/lib/api/series';
import {getNow} from 'src/lib/dates';

export type ForecastTimeSelection = {
  selectedForecastTimes: ForecastTime[];
  setSelectedForecastTimes: (forecastTimes: ForecastTime[]) => void;
  onNextForecastTimesChange: (callback: (forecastTimes: ForecastTime[]) => void) => void;
};

/**
 * Which of `forecastTimes` are selected: the user's pick while they have made one, otherwise the
 * default for the date range. `selectionScope` names what a pick belongs to (a site, a model);
 * when it changes the pick is dropped, since another scope's forecasts are unrelated.
 */
export function useForecastTimeSelection(
  forecastTimes: ForecastTime[] | undefined,
  dateRange: [Date, Date],
  selectionScope: string
): ForecastTimeSelection {
  // The pick carries the scope it was made in, so a pick from another scope reads as "no pick"
  // on the very render the scope changes rather than after an effect has run.
  const [pick, setPick] = React.useState<{scope: string; initializationTimes: number[]}>();
  const [callbacks, setCallbacks] = React.useState<((forecastTimes: ForecastTime[]) => void)[]>([]);

  // undefined = "follow the default"; an explicit array = the user's choice (incl. [])
  const override = pick?.scope === selectionScope ? pick.initializationTimes : undefined;

  // Forget the pick for good, so returning to its scope shows the default again.
  React.useEffect(() => {
    setPick((current) => (current?.scope === selectionScope ? current : undefined));
  }, [selectionScope]);

  const selectedForecastTimes = React.useMemo(() => {
    if (!forecastTimes) return [];
    if (override !== undefined) {
      // A reissued forecast keeps its initialization time but has updated issue metadata.
      return override.flatMap(
        (initializationTime) =>
          forecastTimes.find(
            (available) => available.initializationTime.getTime() === initializationTime
          ) ?? []
      );
    }
    return computeDefaultPrimaryForecastTimes(forecastTimes, dateRange);
  }, [forecastTimes, override, dateRange]);

  // A chance for event handlers to act after the date range change they triggered has landed.
  React.useEffect(() => {
    if (!forecastTimes || callbacks.length === 0) return;
    callbacks.forEach((callback) => callback(forecastTimes));
    setCallbacks([]);
  }, [forecastTimes, callbacks]);

  const onNextForecastTimesChange = React.useCallback(
    (callback: (forecastTimes: ForecastTime[]) => void) =>
      setCallbacks((pending) => [...pending, callback]),
    []
  );

  const setSelectedForecastTimes = React.useCallback(
    (times: ForecastTime[]) =>
      setPick({
        scope: selectionScope,
        initializationTimes: times.map(({initializationTime}) => initializationTime.getTime()),
      }),
    [selectionScope]
  );

  return {
    selectedForecastTimes,
    setSelectedForecastTimes,
    onNextForecastTimesChange,
  };
}

// Default selection: latest when the range shows the future,
// otherwise the middle entry.
export function computeDefaultPrimaryForecastTimes(
  forecastTimes: ForecastTime[],
  dateRange: [Date, Date]
): ForecastTime[] {
  if (getNow() < dateRange[1]) {
    return forecastTimes[0] ? [forecastTimes[0]] : [];
  }
  const middleIndex = Math.ceil(forecastTimes.length / 2);
  return forecastTimes[middleIndex] ? [forecastTimes[middleIndex]] : [];
}
